import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const toSafeMillimeters = (value, fallback, min = 15, max = 120) => {
  const size = Number(value);
  if (!Number.isFinite(size)) return fallback;
  return Math.min(Math.max(size, min), max);
};

const LABEL_PRINTER_SIZE_KEY = "pharmacy.label-printer-size";
// The compact horizontal label shown on the XP-T361U roll.
const DEFAULT_LABEL_PRINTER_SIZE = { widthMm: 50, heightMm: 25 };
const DEFAULT_LABEL_PRINTER_SETTINGS = {
  ...DEFAULT_LABEL_PRINTER_SIZE,
  qrSizeMm: 18,
  topMarginMm: 2,
  bottomMarginMm: 2,
  leftMarginMm: 2,
  rightMarginMm: 2,
  copies: 1,
};

const toSafeNumber = (value, fallback, min, max) =>
  toSafeMillimeters(value, fallback, min, max);

const sanitizeLabelPrinterSettings = (settings, fallback = DEFAULT_LABEL_PRINTER_SETTINGS) => {
  const widthMm = toSafeNumber(settings?.widthMm, fallback.widthMm, 20, 80);
  const heightMm = toSafeNumber(settings?.heightMm, fallback.heightMm, 15, 120);
  // Keep at least 5 mm of printable space on each axis. This prevents invalid
  // combined margins from leaving no room for the QR image.
  const maxHorizontalMargins = Math.max(0, widthMm - 5);
  const leftMarginMm = Math.min(
    toSafeNumber(settings?.leftMarginMm, fallback.leftMarginMm, 0, 20),
    maxHorizontalMargins,
  );
  const rightMarginMm = Math.min(
    toSafeNumber(settings?.rightMarginMm, fallback.rightMarginMm, 0, 20),
    maxHorizontalMargins - leftMarginMm,
  );
  const maxVerticalMargins = Math.max(0, heightMm - 5);
  const topMarginMm = Math.min(
    toSafeNumber(settings?.topMarginMm, fallback.topMarginMm, 0, 20),
    maxVerticalMargins,
  );
  const bottomMarginMm = Math.min(
    toSafeNumber(settings?.bottomMarginMm, fallback.bottomMarginMm, 0, 20),
    maxVerticalMargins - topMarginMm,
  );

  return {
    widthMm,
    heightMm,
    qrSizeMm: toSafeNumber(settings?.qrSizeMm, fallback.qrSizeMm, 5, 75),
    topMarginMm,
    bottomMarginMm,
    leftMarginMm,
    rightMarginMm,
    copies: toSafeNumber(settings?.copies, fallback.copies, 1, 100),
  };
};

export const getLabelPrinterSettings = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(LABEL_PRINTER_SIZE_KEY));
    if (saved) return sanitizeLabelPrinterSettings(saved);
  } catch {
    // Use defaults when storage is unavailable or invalid.
  }
  return DEFAULT_LABEL_PRINTER_SETTINGS;
};

export const setLabelPrinterSettings = (settings) => {
  const previous = getLabelPrinterSettings();
  const next = sanitizeLabelPrinterSettings(settings, previous);
  try {
    window.localStorage.setItem(LABEL_PRINTER_SIZE_KEY, JSON.stringify(next));
  } catch {
    // Printing still uses the selected settings if storage is unavailable.
  }
  return next;
};

export const getLabelPrinterSize = () => {
  const { widthMm, heightMm } = getLabelPrinterSettings();
  return { widthMm, heightMm };
};

export const setLabelPrinterSize = (widthMm, heightMm) => {
  const settings = setLabelPrinterSettings({ widthMm, heightMm });
  return { widthMm: settings.widthMm, heightMm: settings.heightMm };
};

const buildBarcodeLabelDataUrl = (value, sizeMm) => {
  const px = Math.max(220, Math.round(sizeMm * 16));
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, px, px);

  const barCanvas = document.createElement("canvas");
  try {
    JsBarcode(barCanvas, String(value), {
      format: "CODE128",
      height: Math.round(px * 0.62),
      width: 2,
      displayValue: true,
      fontSize: Math.round(px * 0.1),
      margin: 6,
      background: "#ffffff",
      lineColor: "#000000",
    });
  } catch {
    return "";
  }

  const pad = Math.round(px * 0.04);
  const avail = px - pad * 2;
  const scale = Math.min(avail / barCanvas.width, avail / barCanvas.height);
  const drawW = barCanvas.width * scale;
  const drawH = barCanvas.height * scale;
  ctx.drawImage(barCanvas, (px - drawW) / 2, (px - drawH) / 2, drawW, drawH);
  return canvas.toDataURL("image/png");
};

const buildQrDataUrl = (value) =>
  QRCode.toDataURL(String(value), {
    width: 256,
    margin: 2,
    color: { dark: "#000000", light: "#ffffff" },
  });

const renderLockedLabel = (kind, innerHtml) => `
  <section class="label ${kind}-only">
    <div class="print-area">
      ${innerHtml}
    </div>
  </section>
`;

const createLabelMarkup = async ({ barcode, name, mode = "barcode" }, { barcodeBoxMm }) => {
  const code = String(barcode || "").trim();
  if (!code) return "";

  const safeMode = mode === "qr" ? "qr" : "barcode";
  const safeBarcode = escapeHtml(code);

  if (safeMode === "qr") {
    const qrDataUrl = await buildQrDataUrl(code);
    return qrDataUrl
      ? renderLockedLabel(
          "qr",
          `<img class="qr" src="${qrDataUrl}" alt="QR ${safeBarcode}" />`,
        )
      : "";
  }

  const barcodeDataUrl = buildBarcodeLabelDataUrl(code, barcodeBoxMm);
  return barcodeDataUrl
    ? renderLockedLabel(
        "barcode",
        `<div class="barcode-frame"><img class="barcode-img" src="${barcodeDataUrl}" alt="${safeBarcode}" /></div>`,
      )
    : "";
};

/**
 * Print labels as individual physical-label pages in one print job.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr' }[]} labels
 * @param {{ widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabels = async (
  labels,
  { widthMm, heightMm } = {},
) => {
  const printableLabels = (Array.isArray(labels) ? labels : []).filter((label) =>
    Boolean(String(label?.barcode || "").trim()),
  );
  if (!printableLabels.length) return false;

  // XP-T361U supports labels up to 80 mm wide. These values must match the
  // label stock configured in the printer driver.
  const configuredSettings = getLabelPrinterSettings();
  const labelWidthMm = toSafeMillimeters(widthMm, configuredSettings.widthMm, 20, 80);
  const labelHeightMm = toSafeMillimeters(heightMm, configuredSettings.heightMm);
  const printableWidthMm = Math.max(
    1,
    labelWidthMm - configuredSettings.leftMarginMm - configuredSettings.rightMarginMm,
  );
  const printableHeightMm = Math.max(
    1,
    labelHeightMm - configuredSettings.topMarginMm - configuredSettings.bottomMarginMm,
  );
  const qrSizeMm = Math.max(
    1,
    Math.min(configuredSettings.qrSizeMm, printableWidthMm, printableHeightMm),
  );
  // Upright barcode, almost the full 25 mm sticker height, no medicine name.
  const barcodeBoxMm = Math.max(12, Math.min(labelWidthMm, labelHeightMm) - 5);
  const labelsMarkup = (
    await Promise.all(
      printableLabels.map((label) => createLabelMarkup(label, { barcodeBoxMm })),
    )
  ).join("");

  const win = window.open("", "medicine-label-print");
  if (!win) return false;

  win.document.write(`
    <html>
      <head>
        <title>Medicine labels</title>
        <style>
          @page {
            size: ${labelWidthMm}mm ${labelHeightMm}mm;
            margin: 0;
          }
          * { box-sizing: border-box; }
          html, body {
            width: ${labelWidthMm}mm;
            margin: 0;
            padding: 0;
            background: #ffffff;
          }
          body {
            font-family: Arial, sans-serif;
            color: #000000;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .label {
            display: block;
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            max-width: ${labelWidthMm}mm;
            max-height: ${labelHeightMm}mm;
            overflow: hidden;
            padding: 0;
            margin: 0;
            background: #ffffff;
            page-break-after: always;
            break-after: page;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .label:last-of-type {
            page-break-after: auto;
            break-after: auto;
          }
          .print-area {
            width: ${labelWidthMm}mm;
            height: ${labelHeightMm}mm;
            padding: ${configuredSettings.topMarginMm}mm ${configuredSettings.rightMarginMm}mm ${configuredSettings.bottomMarginMm}mm ${configuredSettings.leftMarginMm}mm;
            overflow: hidden;
            text-align: center;
          }
          .qr-only .print-area,
          .barcode-only .print-area {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .barcode-only .print-area {
            padding: 1.5mm;
          }
          .barcode-frame {
            width: ${barcodeBoxMm}mm;
            height: ${barcodeBoxMm}mm;
            overflow: hidden;
          }
          .barcode-img {
            display: block;
            width: ${barcodeBoxMm}mm;
            height: ${barcodeBoxMm}mm;
            object-fit: contain;
            transform: rotate(-90deg);
          }
          .qr {
            display: block;
            width: ${qrSizeMm}mm;
            height: ${qrSizeMm}mm;
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
          }
          @media print {
            html, body {
              width: ${labelWidthMm}mm !important;
            }
            .label,
            .print-area {
              width: ${labelWidthMm}mm !important;
              height: ${labelHeightMm}mm !important;
              margin: 0 !important;
            }
          }
        </style>
      </head>
      <body>
        ${labelsMarkup}
        <script>
          const startPrint = () => {
            const printLabel = () => {
              // Keep this document open. Closing a print window programmatically
              // can cancel a Windows print job before the driver has accepted it.
              window.focus();
              window.print();
            };

            const images = Array.from(document.images);
            Promise.all(
              images.map((image) =>
                image.complete
                  ? Promise.resolve()
                  : new Promise((resolve) => {
                      image.addEventListener("load", resolve, { once: true });
                      image.addEventListener("error", resolve, { once: true });
                    }),
              ),
            ).then(() => window.setTimeout(printLabel, 100));
          };

          if (document.readyState === "complete") startPrint();
          else window.addEventListener("load", startPrint);
        <\/script>
      </body>
    </html>
  `);
  win.document.close();
  return true;
};

/**
 * Print copies of one medicine label. Kept for existing single-label callers.
 * @param {{ barcode: string, name?: string, mode?: 'barcode'|'qr', copies?: number, widthMm?: number, heightMm?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  mode = "barcode",
  copies,
  widthMm,
  heightMm,
}) => {
  const configuredCopies = getLabelPrinterSettings().copies;
  const labelCount = Math.max(1, Number.parseInt(copies ?? configuredCopies, 10) || 1);
  return printMedicineLabels(
    Array.from({ length: labelCount }, () => ({ barcode, name, mode })),
    { widthMm, heightMm },
  );
};

export default printMedicineLabel;
